import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { BusinessHours } from "@/types/database";

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const defaultHours: BusinessHours = {
  monday: { open: "09:00", close: "17:00", is_closed: false },
  tuesday: { open: "09:00", close: "17:00", is_closed: false },
  wednesday: { open: "09:00", close: "17:00", is_closed: false },
  thursday: { open: "09:00", close: "17:00", is_closed: false },
  friday: { open: "09:00", close: "17:00", is_closed: false },
  saturday: { open: "10:00", close: "15:00", is_closed: false },
  sunday: { open: "10:00", close: "15:00", is_closed: true },
};

export function BusinessHoursSettings() {
  const { t } = useTranslation();
  const { selectedStore, fetchStores } = useStoreManagementStore();
  const [isLoading, setIsLoading] = useState(false);
  const [businessHours, setBusinessHours] =
    useState<BusinessHours>(defaultHours);

  useEffect(() => {
    if (selectedStore?.business_hours) {
      // Convert the business hours to match the expected type
      const storeHours = Object.entries(selectedStore.business_hours).reduce(
        (acc, [day, hours]) => ({
          ...acc,
          [day]: {
            open: hours.open || "09:00",
            close: hours.close || "17:00",
            is_closed: hours.closed || false,
          },
        }),
        {} as BusinessHours
      );
      setBusinessHours(storeHours);
    }
  }, [selectedStore]);

  const handleHoursChange = (
    day: keyof BusinessHours,
    field: "open" | "close" | "is_closed",
    value: string | boolean
  ) => {
    setBusinessHours((prev) => {
      const updated = {
        ...prev,
        [day]: {
          ...prev[day],
          [field]: value,
        },
      };
      return updated;
    });
  };

  const handleSave = async () => {
    if (!selectedStore?.id) return;

    setIsLoading(true);
    try {
      // Convert the business hours to match the database schema
      const dbBusinessHours = Object.entries(businessHours).reduce(
        (acc, [day, hours]) => ({
          ...acc,
          [day]: {
            open: hours.open,
            close: hours.close,
            closed: hours.is_closed,
          },
        }),
        {}
      );

      const { data, error } = await supabase
        .from("stores")
        .update({ business_hours: dbBusinessHours })
        .eq("id", selectedStore.id)
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      // Update the local store data
      const updatedStore = {
        ...selectedStore,
        business_hours: dbBusinessHours,
      };

      // Update the store management state
      await fetchStores();

      // Update the selected store in the store management state
      const { setSelectedStore } = useStoreManagementStore.getState();
      setSelectedStore(updatedStore);

      toast.success(t("businessHoursUpdated"));
    } catch (error) {
      toast.error(t("errorUpdatingBusinessHours"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Clock className="h-5 w-5" />
          <CardTitle>{t("businessHours")}</CardTitle>
        </div>
        <CardDescription>{t("businessHoursDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className="flex items-center justify-between">
              <Label className="capitalize min-w-[100px]">
                {t(`days.${day}`)}
              </Label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!businessHours[day].is_closed}
                    onChange={(e) =>
                      handleHoursChange(day, "is_closed", !e.target.checked)
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("open")}
                  </span>
                </div>

                {!businessHours[day].is_closed && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={businessHours[day].open}
                      onChange={(e) =>
                        handleHoursChange(day, "open", e.target.value)
                      }
                      className="w-24"
                    />
                    <span className="text-muted-foreground">{t("to")}</span>
                    <Input
                      type="time"
                      value={businessHours[day].close}
                      onChange={(e) =>
                        handleHoursChange(day, "close", e.target.value)
                      }
                      className="w-24"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? t("saving") : t("saveChanges")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
